import config from '../config';
// Import initialContent
import initialContent from '../initial-content.json';

// API endpoint URL for the content management API
const API_ENDPOINT = config.api.url;

// Get all content sections for a page, ordered by position
export async function getPageContent(pageId) {
  // Get initial content first
  const initialPageContent = initialContent[pageId] || null;
  const initialTimestamp = initialPageContent?.lastUpdated 
    ? new Date(initialPageContent.lastUpdated).getTime() 
    : 0;
  
  try {
    // URL encode the pageId to handle nested paths like 'tracks/smart-charging'
    const encodedPageId = encodeURIComponent(pageId);
    
    // In production, call the actual API
    const response = await fetch(`${API_ENDPOINT}content/${encodedPageId}`);
    
    if (response.ok) {
      const apiContent = await response.json();
      const apiTimestamp = apiContent.lastUpdated 
        ? new Date(apiContent.lastUpdated).getTime() 
        : Date.now();
      
      // Only use API content if it's newer than initial content
      if (apiTimestamp > initialTimestamp) {
        return apiContent;
      } else {
        return initialPageContent.sections || [];
      }
    } else {
      
      // If API fails, check if we have initial content for this page
      if (initialPageContent) {
        return initialPageContent.sections || [];
      }
      
      // If no initial content, return empty array
      return [];
    }
  } catch (error) {
    console.error('Error fetching page content:', error);
    
    // If network error or other issue, check if we have initial content
    if (initialPageContent) {
      return initialPageContent.sections || [];
    }
    
    // If no initial content, return empty array
    return [];
  }
}

// Get a specific content section
export async function getSectionContent(pageId, position) {
  // Convert position to number if it's a string
  const positionNum = parseInt(position);
  
  try {
    // Always use query parameter endpoint for consistency
    const url = `${API_ENDPOINT}content/section?pageId=${encodeURIComponent(pageId)}&position=${position}`;
    
    // In production, call the actual API
    const response = await fetch(url);
    
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.error(`API response not ok: ${response.status} ${response.statusText}`);
      // Fall back to initial content if available
      if (initialContent[pageId] && initialContent[pageId].sections) {
        const section = initialContent[pageId].sections.find(s => s.position === positionNum);
        if (section) {
          return section;
        }
      }
      throw new Error(`Section not found: ${pageId}/${position}`);
    }
  } catch (error) {
    console.error('Error fetching section content:', error);
    
    // Try fallback
    if (initialContent[pageId] && initialContent[pageId].sections) {
      const section = initialContent[pageId].sections.find(s => s.position === positionNum);
      if (section) {
        return section;
      }
    }
    
    throw error;
  }
}

// Update content and record history.
// `options` may include { blockId: 'primary' | 'secondary', layout: 'single' | 'double' }
export async function updateContent(pageId, position, content, contentType = 'html', modifiedBy = 'user', options = {}) {
  try {
    // Always use query parameter endpoint for consistency
    const url = `${API_ENDPOINT}content/section?pageId=${encodeURIComponent(pageId)}&position=${position}`;

    const body = {
      content,
      contentType,
      modifiedBy,
    };
    if (options.blockId) body.blockId = options.blockId;
    if (options.layout) body.layout = options.layout;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      console.error(`HTTP Error ${response.status}:`, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('Error updating content:', error);
    throw error;
  }
}

// Update a structured card-grid section. Sends `blockType: 'card-grid'` and
// a `data` payload (title + cards) to the same update endpoint.
export async function updateCardGridSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'card-grid', data, modifiedBy);
}

// Update a structured winners-list section (title + entries with optional links).
export async function updateWinnersListSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'winners-list', data, modifiedBy);
}

// Update a structured numbered-steps section (title + ordered steps).
export async function updateNumberedStepsSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'numbered-steps', data, modifiedBy);
}

// Generic info-cards block — title + optional intro + cards. Accent (top/left)
// and column count (2/3) are part of the data payload.
export async function updateInfoCardsSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'info-cards', data, modifiedBy);
}

// Stat-cards block — title + optional intro + stats (value + label) + optional footer text/link.
export async function updateStatCardsSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'stat-cards', data, modifiedBy);
}

// Card-steps block — same shape as numbered-steps but each step is wrapped in a card.
export async function updateCardStepsSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'card-steps', data, modifiedBy);
}

// CTA banner — full-width red banner with title, subtitle, and a pill button.
export async function updateCtaBannerSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'cta-banner', data, modifiedBy);
}

// Completed tracks — list of past challenge tracks with winners + external links.
export async function updateCompletedTracksSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'completed-tracks', data, modifiedBy);
}

// FAQ — list of Q&A pairs with thin dividers.
export async function updateFaqSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'faq', data, modifiedBy);
}

// Track partners — symmetric grid of logo + name cards, each linking to an external site.
export async function updateTrackPartnersSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'track-partners', data, modifiedBy);
}

// Submit-solution CTA — bordered panel with title, subtitle, numbered steps and a big button.
export async function updateSubmitCtaSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'submit-cta', data, modifiedBy);
}

// Challenge timeline — vertical timeline with completed/current/future markers + status/duration side cards.
export async function updateChallengeTimelineSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'challenge-timeline', data, modifiedBy);
}

// Awards — prize cards (icon + place label + value) with an optional note underneath.
export async function updateAwardsSection(pageId, position, data, modifiedBy = 'user') {
  return _updateStructuredSection(pageId, position, 'awards', data, modifiedBy);
}

async function _updateStructuredSection(pageId, position, blockType, data, modifiedBy) {
  const url = `${API_ENDPOINT}content/section?pageId=${encodeURIComponent(pageId)}&position=${position}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blockType, data, modifiedBy }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Failed to save ${blockType}: ${response.status} ${text}`);
  }
  return response.json();
}

// Get content history for a section
export async function getContentHistory(pageId, position) {
  try {
    // URL encode the pageId to handle nested paths
    const encodedPageId = encodeURIComponent(pageId);
    const url = `${API_ENDPOINT}content/history/${encodedPageId}/${position}`;
    
    // In production, call the actual API
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`History API error: ${response.status}`);
      throw new Error(`API error: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching content history:', error);
    return [];
  }
}

// Restore content from history
export async function restoreContent(pageId, position, timestamp) {
  try {
    // In production, call the actual API
    const response = await fetch(`${API_ENDPOINT}restore`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageId,
        position,
        timestamp
      }),
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error restoring content:', error);
    throw error;
  }
}

// Update section position
export async function updateSectionPosition(pageId, currentPosition, newPosition) {
  try {
    const response = await fetch(`${API_ENDPOINT}content/position`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageId,
        currentPosition,
        newPosition
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating section position:', error);
    throw error;
  }
}

// Delete section
export async function deleteSection(pageId, position) {
  try {
    // Always use query parameter endpoint for consistency
    const url = `${API_ENDPOINT}content/section?pageId=${encodeURIComponent(pageId)}&position=${position}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting section:', error);
    throw error;
  }
}
